'use client';

/**
 * HomepageV3 — Editorial Hero section (`.se-top` + `.se-hero`).
 *
 * Pixel-ported from `design-refs/source/assets/private-home.css`:
 *   `.se-top`, `.se-hero`, `.se-hero-eyebrow`, `.se-hero h1` / `em`,
 *   `.se-hero-rule`, `.se-hero-cta`, `.se-hero-cta-btn`, `.se-hero-cta-note`,
 *   `.se-hero-stats`, `.se-hero-stat`, `.se-hero-stat-val`, `.se-hero-stat-label`.
 *
 * Dark radial-gradient night background; left column = eyebrow + serif H1 with
 * italic champagne `<em>` accents + gold hairline rule + cream CTA button; right
 * column = 4 stat cards from HERO_STATS. Two-column grid on desktop, stacked on
 * mobile. Mounts inside `EditorialRoot` (publishes `--night`, `--champagne`,
 * `--gold`, `--serif`, `--cream`, `--pad` etc.).
 */
import { styled } from '@mui/material/styles';
import type { MouseEvent } from 'react';
import { HERO, HERO_STATS, type HeroStat } from 'components/HomepageV3/data/demoData';

export interface HeroSectionProps {
  /** Tracked uppercase champagne eyebrow. */
  eyebrow?: string;
  /** Headline lead text (rendered upright). */
  title?: string;
  /** Headline tail rendered italic / champagne via `<em>`. */
  titleEm?: string;
  /** Optional footnote marker (e.g. "1") rendered as a superscript after the title. */
  titleFootnote?: string;
  /** Supporting line below the gold rule (`.se-hero-sub`). */
  subtitle?: string;
  /** Stat cards for the right column. */
  stats?: HeroStat[];
  /** CTA button label. */
  ctaLabel?: string;
  /** Optional href for native navigation/scroll fallback before hydration. */
  ctaHref?: string;
  /** Primary CTA note line. */
  ctaNote?: string;
  /** Secondary CTA note line (rendered in `<span>`). */
  ctaSubNote?: string;
  /** CTA click handler. */
  onCtaClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  /**
   * PD-35962 — analytics tag for the rendered hero variant (slot id /
   * experience, e.g. `se3_default` / `se3_retail`, or `se3_fallback` when the
   * hardcoded demoData hero is rendered because no CMS slot was published).
   */
  heroVariant?: string;
}

const Top = styled('section')({
  background: 'radial-gradient(120% 85% at 18% 0%, #0D2A2C 0%, #07191A 44%, #030D0C 100%)',
  color: '#fff',
});

const Hero = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '44px',
  maxWidth: 1240,
  margin: '0 auto',
  padding: '56px var(--pad) 60px',
  alignItems: 'center',
  justifyContent: 'space-between',
  '@media (min-width: 641px)': {
    gridTemplateColumns: '1fr',
    gap: '40px',
    padding: '56px var(--pad) 52px',
  },
  '@media (min-width: 1081px)': {
    gridTemplateColumns: 'minmax(0, 620px) 430px',
    gap: '72px',
    padding: '50px var(--pad) 40px',
  },
});

const Lead = styled('div')({});

const Eyebrow = styled('p')({
  margin: 0,
  fontFamily: 'var(--sans)',
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  color: 'rgba(233,215,182,0.7)',
  marginBottom: '24px',
  '@media (min-width: 641px)': {
    letterSpacing: '0.42em',
    marginBottom: '26px',
  },
});

const Title = styled('h1')({
  fontFamily: 'var(--serif)',
  fontWeight: 400,
  fontSize: '34px',
  lineHeight: 1.15,
  letterSpacing: '-0.012em',
  color: '#fff',
  margin: 0,
  textWrap: 'pretty',
  maxWidth: 'none',
  '& em': {
    fontStyle: 'italic',
    color: 'var(--champagne)',
  },
  '@media (min-width: 641px)': {
    fontSize: '50px',
    lineHeight: 1.07,
    maxWidth: '18ch',
  },
  '@media (min-width: 1081px)': {
    fontSize: '60px',
    maxWidth: '15ch',
  },
});

const Sup = styled('sup')({
  fontFamily: 'var(--sans)',
  fontSize: '0.32em',
  fontWeight: 500,
  color: 'var(--gold)',
  verticalAlign: 'super',
  marginLeft: '3px',
  lineHeight: 0,
});

const Rule = styled('div')({
  width: '84px',
  height: '1px',
  background: 'var(--gold)',
  margin: '30px 0 28px',
  '@media (min-width: 641px)': {
    margin: '32px 0 30px',
  },
});

const Sub = styled('p')({
  margin: '0 0 34px',
  fontFamily: 'var(--sans)',
  fontSize: '15px',
  fontWeight: 400,
  color: 'var(--c-text-2)',
  lineHeight: 1.7,
  maxWidth: '460px',
  '@media (min-width: 641px)': {
    fontSize: '19.5px',
    lineHeight: 1.62,
    marginBottom: '34px',
  },
});

const Cta = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '18px',
  '@media (min-width: 641px)': {
    flexDirection: 'row',
    alignItems: 'center',
    gap: '24px',
  },
});

const CtaBtn = styled('button')({
  fontFamily: 'var(--sans)',
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'var(--night)',
  background: 'var(--cream)',
  border: 'none',
  padding: '18px',
  borderRadius: '16px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  width: '100%',
  textAlign: 'center',
  boxShadow: '0 18px 40px rgba(0,0,0,0.3)',
  transition: 'transform 0.16s, background 0.16s',
  '&:hover': {
    background: '#fff',
    transform: 'translateY(-2px)',
  },
  '@media (min-width: 641px)': {
    width: 'auto',
    padding: '18px 40px',
  },
});

const CtaLink = styled('a')({
  fontFamily: 'var(--sans)',
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'var(--night)',
  background: 'var(--cream)',
  border: 'none',
  padding: '18px',
  borderRadius: '16px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  width: '100%',
  textAlign: 'center',
  boxShadow: '0 18px 40px rgba(0,0,0,0.3)',
  transition: 'transform 0.16s, background 0.16s',
  textDecoration: 'none',
  '&:hover': {
    background: '#fff',
    color: 'var(--night)',
    transform: 'translateY(-2px)',
    textDecoration: 'none',
  },
  '@media (min-width: 641px)': {
    width: 'auto',
    padding: '18px 40px',
  },
});

const CtaNote = styled('span')({
  fontSize: '11px',
  color: 'var(--c-text-2)',
  letterSpacing: '0.02em',
  lineHeight: 1.5,
  textAlign: 'center',
  whiteSpace: 'normal',
  maxWidth: '100%',
  '& span': {
    display: 'inline',
    color: 'var(--c-text-3)',
    fontSize: '11px',
    marginTop: 0,
  },
  '& span::before': {
    content: '" · "',
  },
  '@media (min-width: 641px)': {
    fontSize: '15px',
    textAlign: 'left',
    whiteSpace: 'normal',
    '& span': {
      display: 'block',
      fontSize: '13px',
      marginTop: '3px',
    },
    '& span::before': {
      content: '""',
    },
  },
});

const Stats = styled('div')({
  display: 'grid',
  // PD-35993 — `minmax(0, 1fr)` lets the nowrap stat values shrink inside the
  // 2-up mobile grid instead of forcing the row past the viewport width.
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: '14px',
  maxWidth: 'none',
  minWidth: 0,
  '@media (min-width: 641px)': {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    maxWidth: '520px',
  },
  '@media (min-width: 1081px)': {
    maxWidth: 'none',
  },
});

const Stat = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  gap: '12px',
  border: '1px solid rgba(233,215,182,0.22)',
  borderRadius: '16px',
  padding: '22px 20px 20px',
  minWidth: 0,
  background: 'linear-gradient(160deg, rgba(252,249,245,0.07), rgba(252,249,245,0.02))',
  '@media (min-width: 641px)': {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
    borderRadius: '18px',
    padding: '20px 26px',
  },
});

const StatVal = styled('div')({
  fontFamily: 'var(--serif)',
  fontWeight: 400,
  fontSize: '28px',
  lineHeight: 1,
  color: '#fff',
  letterSpacing: '-0.01em',
  whiteSpace: 'nowrap',
  '@media (min-width: 641px)': {
    fontSize: '34px',
  },
});

const StatLabel = styled('div')({
  fontSize: '10px',
  fontWeight: 500,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--gold)',
  lineHeight: 1.5,
  textAlign: 'left',
  '@media (min-width: 641px)': {
    letterSpacing: '0.18em',
    textAlign: 'right',
  },
});

export function HeroSection({
  eyebrow = HERO.eyebrow,
  title = HERO.title,
  titleEm = HERO.titleEm,
  titleFootnote,
  subtitle = '',
  stats = HERO_STATS,
  ctaLabel = 'Explore Investments',
  ctaHref,
  ctaNote = 'Free to join · No commitment',
  ctaSubNote = 'Select offerings for accredited investors only',
  onCtaClick,
  heroVariant = 'se3_fallback',
}: HeroSectionProps = {}) {
  return (
    <Top data-cy="hero-section" data-hero-variant={heroVariant} className="se-top">
      <Hero className="se-hero">
        <Lead>
          {eyebrow ? (
            <Eyebrow className="se-hero-eyebrow" data-cy="hero-eyebrow">
              {eyebrow}
            </Eyebrow>
          ) : null}
          <Title data-cy="hero-title">
            {title}
            {titleEm ? (
              <>
                {' '}
                <em>{titleEm}</em>
              </>
            ) : null}
            {titleFootnote ? <Sup data-cy="hero-title-footnote">{titleFootnote}</Sup> : null}
          </Title>
          <Rule className="se-hero-rule" aria-hidden="true" />
          {subtitle ? (
            <Sub className="se-hero-sub" data-cy="hero-sub">
              {subtitle}
            </Sub>
          ) : null}
          <Cta className="se-hero-cta">
            {ctaHref ? (
              <CtaLink
                href={ctaHref}
                className="se-hero-cta-btn"
                data-cy="hero-cta"
                onClick={onCtaClick}
              >
                {ctaLabel}
              </CtaLink>
            ) : (
              <CtaBtn
                type="button"
                className="se-hero-cta-btn"
                data-cy="hero-cta"
                onClick={onCtaClick}
              >
                {ctaLabel}
              </CtaBtn>
            )}
            <CtaNote className="se-hero-cta-note" data-cy="hero-cta-note">
              {ctaNote}
              <span>{ctaSubNote}</span>
            </CtaNote>
          </Cta>
        </Lead>

        <Stats className="se-hero-stats" data-cy="hero-stats">
          {stats.map((stat) => (
            <Stat className="se-hero-stat" data-cy="hero-stat" key={stat.label}>
              <StatVal className="se-hero-stat-val" data-cy="hero-stat-val">
                {stat.val}
              </StatVal>
              <StatLabel className="se-hero-stat-label" data-cy="hero-stat-label">
                {stat.label}
              </StatLabel>
            </Stat>
          ))}
        </Stats>
      </Hero>
    </Top>
  );
}

export default HeroSection;
